import React, { FunctionComponent, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { ButtonModifier } from '../../atoms/baseButton/BaseButton'
import { SelectField } from '../../atoms/formElements/SelectField'
import { IconButton } from '../../atoms/iconButton/IconButton'
import { ClockIcon } from '../../atoms/icons/ClockIcon'
import { TrashIcon } from '../../atoms/icons/TrashIcon'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderCalendarTimeSlotSelectorProps = {
  index: number
  allOptions: { label: string; value: string }[]
  blockedTimeSlotValues: { [key: string]: string[] }
  showDeleteIcon: boolean
  selectedOption: string
  selectedOptionList: string[]
  setSelectedOptionList: (startTimes: string[]) => void
  singleTimeSelector: boolean
  error?: string
  onFocus?: () => void
  onBlur?: () => void
}

const TimeSlotSelectionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignSelf: 'stretch',
  gap: theme.spacing['16px'],
}))

const TimeSlotInputContainer = styled.View<{ singleTimeSelector: boolean }>(({ singleTimeSelector }) => ({
  flexDirection: 'column',
  justifyContent: 'center',
  ...(singleTimeSelector ? { width: '100%' } : { flexGrow: 1 }),
}))

const DeleteIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['8px'],
  width: '40px',
}))

export const ProviderCalendarTimeSlotSelector: FunctionComponent<ProviderCalendarTimeSlotSelectorProps> = ({
  allOptions,
  index,
  blockedTimeSlotValues,
  showDeleteIcon,
  selectedOption,
  selectedOptionList,
  setSelectedOptionList,
  singleTimeSelector,
  onFocus,
  onBlur,
  error,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const hasSetValueNonEmptyValue = useRef(false)
  const updateSelectedOption = (newValue: string, index: number) => {
    if (!hasSetValueNonEmptyValue.current) {
      if (newValue === '') {
        return
      }
      hasSetValueNonEmptyValue.current = true
    }
    const startTimes = [...selectedOptionList]
    startTimes[index] = newValue
    setSelectedOptionList(startTimes)
  }

  const possibleTimeSelections = useMemo(() => {
    const filteredTimeslots = isEmpty(selectedOption)
      ? allOptions.filter((timeSlot) => !Object.keys(blockedTimeSlotValues).includes(timeSlot.value))
      : allOptions.filter(
          (timeSlot) =>
            !Object.keys(blockedTimeSlotValues).includes(timeSlot.value) ||
            (blockedTimeSlotValues[timeSlot.value].length === 1 &&
              blockedTimeSlotValues[timeSlot.value].includes(selectedOption)),
        )
    return filteredTimeslots
  }, [allOptions, blockedTimeSlotValues, selectedOption])
  return (
    <TimeSlotSelectionContainer
      key={selectedOption}
      testID={tID(`ProviderCalendarSlotSelection-timeSlotContainer-${index}`)}
    >
      <TimeSlotInputContainer singleTimeSelector={singleTimeSelector}>
        <SelectField
          accessibilityLabel={formatMessage({
            defaultMessage: 'Time slot selector',
            description: 'Input to select a timeslot for the new availability event',
          })}
          value={selectedOption}
          name={`selectedStartTime-${index}`}
          onChange={(e: string) => updateSelectedOption(e, index)}
          icon={<ClockIcon size={24} fillColor={colors.iconDefault} />}
          options={possibleTimeSelections}
          error={error}
          baseInputStyle={{ marginBottom: 0 }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </TimeSlotInputContainer>
      <DeleteIconContainer>
        {showDeleteIcon && (
          <IconButton
            leftIcon={<TrashIcon size={24} fillColor={colors.iconDefault} />}
            onPress={() => {
              const newStartTimes = [...selectedOptionList]
              newStartTimes.splice(index, 1)
              setSelectedOptionList(newStartTimes)
            }}
            modifier={ButtonModifier.UNSTYLED}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Remove availability timeslot',
              description: 'Button to remove the availability timeslot',
            })}
            textAlign='center'
            testID={tID(`ProviderCalendarSlotSelection-removeTimeslot-${index}`)}
          />
        )}
      </DeleteIconContainer>
    </TimeSlotSelectionContainer>
  )
}
